import { useState, useEffect, useCallback, useRef } from "react";

/**
 * Auto-rotates through trees for kiosk display.
 * Each tree is shown for `intervalMs` before advancing.
 * Supports manual navigation with auto-pause/resume.
 */
export default function useTreeRotation(treeCount, intervalMs = 30000) {
  const [currentTreeIndex, setCurrentTreeIndex] = useState(0);
  const pauseTimeoutRef = useRef(null);
  const [paused, setPaused] = useState(false);

  // Auto-rotation
  useEffect(() => {
    if (treeCount <= 1 || paused) return;
    const timer = setInterval(() => {
      setCurrentTreeIndex((prev) => (prev + 1) % treeCount);
    }, intervalMs);
    return () => clearInterval(timer);
  }, [treeCount, intervalMs, paused]);

  // Reset when tree count changes
  useEffect(() => {
    setCurrentTreeIndex(0);
  }, [treeCount]);

  const pauseTemporarily = useCallback(() => {
    setPaused(true);
    if (pauseTimeoutRef.current) clearTimeout(pauseTimeoutRef.current);
    pauseTimeoutRef.current = setTimeout(() => setPaused(false), 60000);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (pauseTimeoutRef.current) clearTimeout(pauseTimeoutRef.current);
    };
  }, []);

  const goToNext = useCallback(() => {
    setCurrentTreeIndex((prev) => (prev + 1) % treeCount);
    pauseTemporarily();
  }, [treeCount, pauseTemporarily]);

  const goToPrev = useCallback(() => {
    setCurrentTreeIndex((prev) => (prev - 1 + treeCount) % treeCount);
    pauseTemporarily();
  }, [treeCount, pauseTemporarily]);

  const goToIndex = useCallback(
    (index) => {
      if (index >= 0 && index < treeCount) {
        setCurrentTreeIndex(index);
        pauseTemporarily();
      }
    },
    [treeCount, pauseTemporarily]
  );

  return { currentTreeIndex, goToNext, goToPrev, goToIndex };
}
